// Imports => React
import { useMemo } from "react";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import clsx from "clsx";

import {
  AcIcon,
  AcTooltip,
  DATETIME_FORMATS,
  ICONS,
  THEMES,
} from "@opp/ui-kit";

// Imports => Utilities
import { AcIsSet, AcIsArray } from "@opp/ui-kit";
import { AcFormatCurrencyAmount, AcFormatDate } from "@utils";

// Imports => Tabs
import SettlementDetailsTabs from "@tabs/settlement-details";

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcHeading,
  AcRichContent,
  AcCard,
  AcLoaderInline,
  AcEmptyBlock,
  AcBadge,
  AcDefinitionList,
  AcTabContainer,
} from "@opp/ui-kit";
import { withTranslation } from "react-i18next";

import { AcGetDateIsInFuture } from "../../../utilities/ac-format-date";
import moment from "moment";

import { useGetSettlementsByUidQuery } from "@src/api/openapi/merchantApi";

const _CLASSES = {
  MAIN: "ac-settlement-details-details-tab ac-settlement__grid-container",
};

const AcSettlementDetailsDetailsTab = ({
  store: { profile, ui, settings },
  data: { uid },
  i18n,
  t,
}) => {
  const { data: response, isFetching: isLoading } = useGetSettlementsByUidQuery(
    { uid: uid, expand: "specifications.source" }
  );
  const data = response?.data;

  const { current_partner_currency } = profile;

  const { has_continuous_settlement_interval } = settings;

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN);
  });

  const renderSpecificationTabs = useMemo(() => {
    if (isLoading) {
      return <AcLoaderInline loading />;
    }

    if (
      !AcIsSet(data) ||
      !AcIsSet(data.specifications) ||
      !AcIsArray(data.specifications) ||
      data.specifications.length === 0
    ) {
      return (
        <AcEmptyBlock
          message={t(
            "no-specifications-found",
            "No specifications found to display",
            { ns: "payouts" }
          )}
        />
      );
    }

    const collection = data.specifications;
    const len = collection.length;
    let n = 0;
    let result = [];

    for (n; n < len; n++) {
      const item = collection[n];

      const {
        uid,
        total_number_of_transactions,
        source: { name },
      } = item;

      const object = {
        id: uid,
        label: `${name} (${total_number_of_transactions})`,
        disabled: false,
        component: (
          <SettlementDetailsTabs.Specification
            settlementUid={data.uid}
            settlement={data}
            specificationUid={item.uid}
          />
        ),
        allowed: null,
      };

      result.push(object);
    }

    return (
      <AcTabContainer collection={result} data={null} selectForMobile={true} />
    );
  }, [data, isLoading]);

  const getStatusBadge = useMemo(() => {
    if (!AcIsSet(data)) {
      return null;
    }

    const { status } = data;

    let label = t(`status.${status}`, { ns: "payouts" });
    let theme = null;

    switch (status) {
      case "completed":
      case "paid":
        theme = THEMES.CHARLIE_LIGHT;
        break;

      case "transfer":
        theme = THEMES.ALPHA;
        break;

      case "current":
        theme = THEMES.OMEGA_LIGHT;
        break;

      case "expected":
      case "next":
        theme = THEMES.ECHO;
        break;

      case "cancelled":
        theme = THEMES.BETA_LIGHT;
        break;

      case "invoiced":
        theme = THEMES.ALPHA_DARK;
        break;

      default:
    }

    return <AcBadge theme={theme}>{label}</AcBadge>;
  }, [data]);

  const renderMainFeatures = useMemo(() => {
    if (!AcIsSet(data)) {
      return null;
    }

    const { totalAmount, specifications, total_number_of_transactions } = data;

    const totalNumberOfSpecifications = specifications.length;
    //TODO currency
    const amount = AcFormatCurrencyAmount(
      totalAmount,
      current_partner_currency,
      i18n.resolvedLanguage,
      true
    );
    let content = "";
    if (totalNumberOfSpecifications > 1) {
      content += `<p><span class="h-text--color-medium">${t(
        "based-on",
        "Based on",
        { ns: "payouts" }
      )} </span> <strong>${totalNumberOfSpecifications} ${t("profile", {
        count: totalNumberOfSpecifications,
        ns: "payouts",
      })}
        </strong></p>`;
    }
    content += `<p><span class="h-text--color-medium">${t(
      "contains",
      "Contains",
      { ns: "payouts" }
    )} </span> <strong>${total_number_of_transactions} ${t("transaction", {
      count: total_number_of_transactions,
      ns: "payouts",
    })}
        </strong></p>`;

    return (
      <AcContainer flex grow>
        <AcRow className={"h-margin-bottom-auto"}>
          <AcColumn>
            <AcHeading rank={2} theme={THEMES.DARK}>
              {amount}
            </AcHeading>
          </AcColumn>
        </AcRow>

        <AcRow className={"h-margin-top-auto"}>
          <AcColumn>
            <AcRichContent content={content} />
          </AcColumn>
        </AcRow>
      </AcContainer>
    );
  }, [data, isLoading, i18n.resolvedLanguage, current_partner_currency]);

  const renderStatus = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.status)) {
      return (
        <AcRichContent
          content={`<p><em>${t(
            "no-status-information-available",
            "No status information available",
            { ns: "payouts" }
          )}</em></p>`}
        />
      );
    }

    const {
      created,
      updated,
      period_start,
      period_end,
      payout_type,
      interval,
    } = data;
    let end;

    if (
      interval === "continuous" &&
      AcGetDateIsInFuture(moment(period_end * 1000))
    ) {
      end = t("infinite", "Infinite", { ns: "payouts" });
    } else {
      end = AcFormatDate(
        period_end * 1000,
        null,
        DATETIME_FORMATS.FULL_DATE_SHORT
      );
    }

    const date_format =
      window.outerWidth <= 768
        ? DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES_SHORT
        : DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES;

    const period = {
      start: AcFormatDate(
        period_start * 1000,
        null,
        DATETIME_FORMATS.FULL_DATE_SHORT
      ),
      end,
    };

    let settlement_type = t("single-payout", "Single payout", {
      ns: "payouts",
    });
    if (AcIsSet(payout_type) && payout_type === "collective") {
      settlement_type = t("batch-payout", "Batch payout", { ns: "payouts" });
    }

    let statusBadge;
    if (payout_type !== "faster" && payout_type !== "instant") {
      statusBadge = {
        label: `${t("status-label", "Status", { ns: "common" })}`,
        value: getStatusBadge,
      };
    } else {
      statusBadge = {
        label: `${t("status-label", "Status", { ns: "common" })}`,
        value: "-",
      };
    }
    const result = {
      status: [
        {
          label: `${t("type", "Type", { ns: "common" })}`,
          value: settlement_type,
        },
        statusBadge,
        {
          label: `${t("payout-period", "Payout period", { ns: "payouts" })}`,
          value: `${period.start} - ${period.end}`,
        },
      ],
      details: [
        {
          label: `${t("created-at", "Created at", { ns: "common" })}`,
          value: AcFormatDate(created * 1000, null, date_format),
        },
        {
          label: `${t("last-update", "Last update", { ns: "payouts" })}`,
          value: AcFormatDate(updated * 1000, null, date_format),
        },
      ],
    };

    return <AcDefinitionList items={result.status} />;
  }, [data, getStatusBadge, has_continuous_settlement_interval, i18n.resolvedLanguage]);

  const getBreakdownLabel = (key) => {
    return (
      <AcRow>
        <AcColumn>
          <span>{t("breakdown." + key, { ns: "payouts" })}</span>
          <AcTooltip
            content={`${t("breakdown.tooltip." + key, { ns: "payouts" })}`}
            trigger="hover"
            className="h-margin-left-5 d-md-inline d-none"
            placement="top"
          >
            <span>
              <AcIcon icon={ICONS.HELP_CIRCLE_OUTLINE} />
            </span>
          </AcTooltip>
        </AcColumn>
      </AcRow>
    );
  };

  const renderBreakdown = useMemo(() => {
    if (!AcIsSet(data)) {
      return (
        <AcRichContent
          content={`<p><em>${t(
            "breakdown.no-breakdown-information",
            "No breakdown information available",
            { ns: "payouts" }
          )}</em></p>`}
        />
      );
    }
    const {
      total_amount,
      total_gateway_fees,
      total_order_fees,
      total_refund_fees,
      total_volume,
      total_transaction_costs,
    } = data;

    const totalFees = total_gateway_fees + total_order_fees + total_refund_fees;
    const totalCosts = totalFees + total_transaction_costs;
    //todo currency from settlement
    const currency = current_partner_currency;
    const result = [
      {
        label: `${t("breakdown.total-volume", { ns: "payouts" })}`,
        value: AcFormatCurrencyAmount(
          total_volume,
          currency,
          i18n.resolvedLanguage,
          true
        ),
      },
      {
        label: `${t("breakdown.total-transaction-cost", { ns: "payouts" })}`,
        value: AcFormatCurrencyAmount(
          totalCosts,
          currency,
          i18n.resolvedLanguage,
          true
        ),
      },
      {
        label: `<strong>${t("breakdown.you-receive", "You receive", {
          ns: "payouts",
        })}</strong>`,
        value: `<strong>${AcFormatCurrencyAmount(
          total_amount,
          currency,
          i18n.resolvedLanguage,
          true
        )}</strong>`,
      },
    ];

    return <AcDefinitionList items={result} />;
  }, [data, getStatusBadge, i18n.resolvedLanguage, current_partner_currency]);

  if (isLoading) {
    return <AcLoaderInline loading />;
  }
  return (
    <div className={getMainClassNames}>
      <AcCard className={"ac-settlement__column"}>{renderMainFeatures}</AcCard>

      <AcCard className={"ac-settlement__column"}>
        <AcHeading theme={THEMES.DARK} rank={5} tag={"h3"}>
          {t("details", "Details", { ns: "payouts" })}
        </AcHeading>
        {renderStatus}
      </AcCard>

      <AcCard className={"ac-settlement__column"}>
        <AcHeading theme={THEMES.DARK} rank={5} tag={"h3"}>
          {t("breakdown.breakdown", "Breakdown", { ns: "payouts" })}
        </AcHeading>
        {renderBreakdown}
      </AcCard>

      <AcContainer
        className={"ac-settlement__column ac-settlement__column--full"}
        fluid
      >
        <AcRow className={"h-margin-top-20"}>
          <AcColumn xs={12}>
            <AcHeading theme={THEMES.MEDIUM} rank={6} tag={"h2"}>
              {t("TITLES.SPECIFICATIONS", "Specifications", { ns: "titles" })}
            </AcHeading>
          </AcColumn>
        </AcRow>

        <AcRow>
          <AcColumn xs={12}>{renderSpecificationTabs}</AcColumn>
        </AcRow>
      </AcContainer>
    </div>
  );
};

export default withTranslation(["payouts", "titles"])(
  withStore(observer(AcSettlementDetailsDetailsTab))
);
